import React, { useState, useMemo, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Box, 
  Typography, 
  Select, 
  MenuItem, 
  IconButton, 
  FormControl,
  Stack
} from '@mui/material';
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext
} from '@mui/icons-material';

// Custom Pagination Component
const CustomPagination = ({
  page,
  pageSize,
  rowCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100]
}) => {
  const totalPages = Math.ceil(rowCount / pageSize);
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, rowCount);

  const handleFirstPage = () => onPageChange(0);
  const handleLastPage = () => onPageChange(totalPages - 1);
  const handleNextPage = () => onPageChange(page + 1);
  const handlePreviousPage = () => onPageChange(page - 1);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderTop: '1px solid #E5E7EB',
        backgroundColor: '#F8F9FF',
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        minHeight: '56px',
      }}
    >
      {/* Rows per page selector */}
      <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          عدد الصفوف في الصفحة:
        </Typography>
        <FormControl size="small">
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            sx={{
              fontSize: '0.875rem',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              color: '#1E1B4B',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E5E7EB',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4338CA',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4338CA',
              },
              '& .MuiSelect-select': {
                padding: '4px 8px',
              },
            }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Page info */}
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.875rem',
          fontWeight: 500,
          color: '#6B7280',
          fontFamily: 'Cairo, Tajawal, sans-serif',
        }}
      >
        عرض {startRow.toLocaleString('en-US')}–{endRow.toLocaleString('en-US')} من {rowCount.toLocaleString('en-US')}
      </Typography>

      {/* Navigation buttons */}
      <Stack direction="row" alignItems="center">
        <IconButton
          onClick={handleFirstPage}
          disabled={page === 0}
          size="small"
          title="الصفحة الأولى"
          sx={{ margin: '0 2px', color: '#4338CA', '&.Mui-disabled': { color: '#D1D5DB' } }}
        >
          <LastPage fontSize="small" />
        </IconButton>
        <IconButton
          onClick={handlePreviousPage}
          disabled={page === 0}
          size="small"
          title="الصفحة السابقة"
          sx={{ margin: '0 2px', color: '#4338CA', '&.Mui-disabled': { color: '#D1D5DB' } }}
        >
          <NavigateNext fontSize="small" />
        </IconButton>
        <IconButton
          onClick={handleNextPage}
          disabled={page >= totalPages - 1}
          size="small"
          title="الصفحة التالية"
          sx={{ margin: '0 2px', color: '#4338CA', '&.Mui-disabled': { color: '#D1D5DB' } }}
        >
          <NavigateBefore fontSize="small" />
        </IconButton>
        <IconButton
          onClick={handleLastPage}
          disabled={page >= totalPages - 1}
          size="small"
          title="الصفحة الأخيرة"
          sx={{ margin: '0 2px', color: '#4338CA', '&.Mui-disabled': { color: '#D1D5DB' } }}
        >
          <FirstPage fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
};

const DynamicDataGrid = ({
  columns,
  rows,
  loading = false,
  rowsPerPageOptions = [10, 25, 50, 100],
  pagination = true,
  defaultPageSize = 10,
  paginationProps = {},
  checkboxSelection = false,
  onSelectionModelChange,
  selectionModel = [],
  onRowClick,
  // Server-side pagination support (opt-in). When paginationMode="server",
  // the parent owns paginationModel/rowCount and reacts to onPaginationModelChange.
  paginationMode = 'client',
  paginationModel: controlledPaginationModel,
  onPaginationModelChange: onControlledPaginationModelChange,
  rowCount: controlledRowCount,
  ...otherProps
}) => {
  const isServerMode = paginationMode === 'server';

  const [internalPaginationModel, setInternalPaginationModel] = useState({
    page: 0,
    pageSize: defaultPageSize,
  });

  const paginationModel = isServerMode ? controlledPaginationModel : internalPaginationModel;
  const setPaginationModel = isServerMode ? onControlledPaginationModelChange : setInternalPaginationModel;

  // Handle selection changes from DataGrid
  // Use uncontrolled selection - let DataGrid manage its own state
  const handleSelectionChange = useMemo(() => {
    if (!checkboxSelection || !onSelectionModelChange) {
      return undefined;
    }
    return (newSelection) => {
      // Handle different selection formats from DataGrid v8
      let selection = [];
      if (Array.isArray(newSelection)) {
        // Standard array format
        selection = newSelection;
      } else if (newSelection && typeof newSelection === 'object') {
        // DataGrid v8 might send {type: 'include', ids: Set(...)} format
        if (newSelection.ids && newSelection.ids instanceof Set) {
          selection = Array.from(newSelection.ids);
        } else if (newSelection.ids && Array.isArray(newSelection.ids)) {
          selection = newSelection.ids;
        } else if (newSelection instanceof Set) {
          selection = Array.from(newSelection);
        } else {
          // Try to extract any array-like property
          const keys = Object.keys(newSelection);
          if (keys.length > 0 && Array.isArray(newSelection[keys[0]])) {
            selection = newSelection[keys[0]];
          }
        }
      }
      // Notify parent component
      onSelectionModelChange(selection);
    };
  }, [checkboxSelection, onSelectionModelChange]);

  if (!columns || !Array.isArray(columns) || columns.length === 0) {
    console.warn("لم يتم توفير أعمدة لعنصر DataGrid");
  }

  if (!rows || !Array.isArray(rows)) {
    console.warn("لم يتم توفير صفوف لعنصر DataGrid");
  }

  const safeRows = rows || [];

  const localeText = {
    noRowsLabel: 'لا توجد بيانات مطابقة لبحثك.',
    toolbarDensity: 'الكثافة',
    toolbarColumns: 'الأعمدة',
    toolbarFilters: 'الفلاتر',
    toolbarExport: 'تصدير',
    toolbarExportCSV: 'تصدير كـ CSV',
    toolbarExportPrint: 'طباعة',
    filterPanelOperators: 'المعاملات',
    filterPanelColumns: 'الأعمدة',
    filterPanelInputLabel: 'القيمة',
    filterOperatorContains: 'يحتوي على',
    filterOperatorEquals: 'يساوي',
    filterOperatorStartsWith: 'يبدأ بـ',
    filterOperatorEndsWith: 'ينتهي بـ',
    filterOperatorIsEmpty: 'فارغ',
    filterOperatorIsNotEmpty: 'غير فارغ',
    filterOperatorIsAnyOf: 'أحد القيم التالية',
    columnMenuSortAsc: 'ترتيب تصاعدي',
    columnMenuSortDesc: 'ترتيب تنازلي',
    columnMenuFilter: 'تصفية',
    columnMenuHideColumn: 'إخفاء العمود',
    columnMenuManageColumns: 'إدارة الأعمدة',
    columnsPanelTextFieldLabel: 'البحث في الأعمدة',
    columnsPanelTextFieldPlaceholder: 'عنوان العمود',
    columnsPanelShowAllButton: 'إظهار الكل',
    columnsPanelHideAllButton: 'إخفاء الكل',
    footerRowSelected: (count) =>
      count !== 1
        ? `${count.toLocaleString('en-US')} صفوف محددة`
        : `${count.toLocaleString('en-US')} صف محدد`,
    footerTotalRows: 'إجمالي الصفوف:',
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handlePageChange = (newPage) => {
    setPaginationModel({ ...paginationModel, page: newPage });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPaginationModel({ page: 0, pageSize: newPageSize });
  };

  // Build DataGrid props conditionally
  const dataGridProps = useMemo(() => {
    const baseProps = {
      columns,
      rows: safeRows,
      loading,
      
      // Modern pagination props
      paginationModel,
      onPaginationModelChange: handlePaginationModelChange,
      pageSizeOptions: rowsPerPageOptions,
      pagination,
      ...(isServerMode && {
        paginationMode: 'server',
        rowCount: controlledRowCount,
      }),

      // Hide default footer to use custom one
      hideFooter: true,
      
      sx: {
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        minWidth: 320,
        maxWidth: '100vw',
        overflowX: 'auto',
        border: '1px solid #E5E7EB',
        borderRadius: 3,
        bgcolor: '#fff',
        color: '#1E1B4B',
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: '#F1F5F9',
          borderBottom: '1px solid #E5E7EB',
          fontSize: '0.95rem',
          textAlign: 'center',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 700,
          color: '#1E1B4B',
          fontFamily: 'Cairo, Tajawal, sans-serif',
        },
        '& .MuiDataGrid-columnSeparator': {
          color: '#E5E7EB',
        },
        '& .MuiDataGrid-cell': {
          fontSize: '0.875rem',
          padding: '6px',
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          minWidth: 80,
          maxWidth: '100vw',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          borderColor: '#E5E7EB',
          color: '#1E1B4B',
          fontFamily: 'Cairo, Tajawal, sans-serif',
        },
        '& .MuiDataGrid-row': {
          height: '56px',
          ...(onRowClick && { cursor: 'pointer' }),
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: '#F8F9FF',
        },
        '& .MuiDataGrid-row.Mui-selected': {
          backgroundColor: '#EEF2FF',
        },
        '& .MuiDataGrid-row.Mui-selected:hover': {
          backgroundColor: '#E0E7FF',
        },
        '& .MuiCheckbox-root.Mui-checked': {
          color: '#4338CA',
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: '1px solid #E5E7EB',
        },
        '& .MuiDataGrid-toolbarContainer': {
          fontFamily: 'Cairo, Tajawal, sans-serif',
          '& .MuiButton-root': {
            color: '#4338CA',
            fontFamily: 'Cairo, Tajawal, sans-serif',
          },
        },
        '& .MuiDataGrid-overlay': {
          color: '#6B7280',
          fontFamily: 'Cairo, Tajawal, sans-serif',
        },
        '& .even': {
          backgroundColor: '#fff',
        },
        '& .odd': {
          backgroundColor: '#F8F9FF',
        },
      },
      
      getRowClassName: (params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd',
      
      disableColumnSelector: true,
      disableColumnResize: true,
      sortingOrder: ['asc', 'desc'],
      autoHeight: true,
      localeText,

      ...(onRowClick && { onRowClick }),

      ...otherProps,
    };
    
    // Add selection-related props only when checkboxSelection is enabled
    if (checkboxSelection && handleSelectionChange) {
      // Use uncontrolled selection - don't pass rowSelectionModel
      // This lets DataGrid manage its own selection state internally
      return {
        ...baseProps,
        checkboxSelection: true,
        disableRowSelectionOnClick: false,
        onRowSelectionModelChange: handleSelectionChange,
        // Ensure DataGrid can identify rows properly
        getRowId: (row) => row.id || row.uuid || row._id || JSON.stringify(row),
        // Explicitly don't pass rowSelectionModel to avoid initialization errors
      };
    } else {
      return {
        ...baseProps,
        checkboxSelection: false,
        disableRowSelectionOnClick: true,
        isRowSelectable: () => false,
        // Still provide getRowId for consistency
        getRowId: (row) => row.id || row.uuid || row._id || JSON.stringify(row),
      };
    }
  }, [
    columns,
    safeRows,
    loading,
    checkboxSelection,
    paginationModel,
    handlePaginationModelChange,
    rowsPerPageOptions,
    pagination,
    handleSelectionChange,
    localeText,
    onRowClick,
    otherProps,
    isServerMode,
    controlledRowCount,
  ]);

  // 🎨 Design System Applied
  return (
    <Box sx={{ width: '100%', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
      <Box sx={{ minHeight: '63vh', width: '100%', overflowX: 'auto', maxWidth: '100vw' }}>
        <DataGrid {...dataGridProps} />
      </Box>
      
      {/* Custom Pagination Footer */}
      {pagination && (
        <CustomPagination
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          rowCount={isServerMode ? controlledRowCount : safeRows.length}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={rowsPerPageOptions}
        />
      )}
    </Box>
  );
};

export default DynamicDataGrid;